const track = (event) => {
	try {
		window.dataLayer.push(event);
	} catch (err) {
		console.warn('Data layer not found');
	}
};

class HeroProducts extends HTMLElement {
	constructor() {
		super();

		this.elements = {
			hero: this.closest('[data-featured-product-hero]'),
			productForms: this.querySelectorAll('form'),
			productCartButtons: this.querySelectorAll(
				'[data-hero-product-cart-button]',
			),
			accordionBody: this.querySelector('[data-hero-product-wrapper]'),
			accordionToggle: this.querySelector('[data-hero-product-toggle]'),
			items: this.querySelectorAll('hero-product-item'),
		};

		this.isOpen =
			this.elements.accordionBody.classList.contains('is-open') ||
			(this.elements.accordionBody.classList.contains('is-open--desktop') &&
				!this.isMobile);

		this.heroFooterLabels = {
			openLabel: this.elements.accordionToggle.dataset.openLabel,
			closeLabel: this.elements.accordionToggle.dataset.closeLabel,
		};

		this.isMobile = window.matchMedia('(max-width: 767px)').matches;

		this.toggleAccordion = this.toggleAccordion.bind(this);
	}

	connectedCallback() {
		this.elements.accordionToggle.addEventListener(
			'click',
			this.toggleAccordion,
		);

		this.observer = this.setupObserver();

		const mql = '(min-width: 768px)';
		window.matchMedia('(min-width: 768px)').addEventListener('change', (evt) => {
			this.closeAccordion();
			this.calculateHeights();
		});

		this.calculateHeights();
	}

	setupObserver() {
		let observer;

		let options = {
			root: null,
			rootMargin: '0px',
			threshold: 0,
		};

		const callback = (entries, observer) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting && entry.intersectionRatio <= 0) {
					this.closeAccordion();
				}
			});
		};

		observer = new IntersectionObserver(callback, options);

		return observer;
	}

	toggleAccordion() {
		if (this.isOpen) {
			this.closeAccordion();
		}
		else {
			this.openAccordion();
		}
	}

	calculateHeights() {
		this.elements.accordionBody.classList.add('is-preparing');
		const heights = [...this.elements.items].map((el) => {
			return el.getBoundingClientRect().height;
		});

		const mobile = heights.sort((a, b) => { return a - b; })[0];
		const desktop = heights.reduce((acc, height) => acc += height, 0);

		this.elements.accordionBody.classList.remove('is-preparing');
		this.setAttribute('style', `--mobileHeight: ${mobile}px; --desktopHeight: ${desktop}px`);
	}

	closeAccordion() {
		this.isOpen = false;

		this.elements.accordionBody.classList.remove('is-open', 'is-open--desktop');
		this.elements.accordionToggle.classList.remove(
			'is-open',
			'is-open--desktop',
		);

		this.elements.accordionBody.setAttribute('aria-hidden', true);
		this.elements.accordionToggle.innerText = this.heroFooterLabels.openLabel;

		this.resetForms();
		this.observer.disconnect(this.elements.hero);
	}

	openAccordion() {
		this.isOpen = true;

		this.elements.accordionBody.classList.add('is-open');
		this.elements.accordionBody.setAttribute('aria-hidden', false);
		this.elements.accordionToggle.classList.add('is-open');

		this.elements.accordionToggle.innerText = this.heroFooterLabels.closeLabel;

		this.observer.observe(this.elements.hero);
		track({
			name: 'homepage_promo_modal',
			action: 'opened'
		});
	}

	resetForms() {
		this.elements.productForms.forEach((form, index) => {
			form.reset();
			this.elements.productCartButtons[index].disabled = true;
		});
	}
}

if (!customElements.get('hero-products')) {
	customElements.define('hero-products', HeroProducts);
}

class HeroProduct extends HTMLElement {
	constructor() {
		super();
		this.elements = {
			productForm: this.querySelector('form'),
			productButton: this.querySelector('[data-hero-product-cart-button]'),
			links: this.querySelectorAll('a[href]'),
		};
	}

	connectedCallback() {
		this.elements.productForm.addEventListener('change', () => {
			this.elements.productButton.disabled = false;
		});

		this.elements.productForm.addEventListener('submit', (evt) =>
			this.addProductToCart(evt),
		);

		this.elements.links.forEach((link) => {
			link.addEventListener('click', (evt) => {
				track({
					name: 'homepage_promo_modal',
					action: 'link-followed'
				});
			});
		});
	}

	async addProductToCart(evt) {
		evt.preventDefault();
		const cartRefreshEvent = new CustomEvent('cartDrawer:refreshed');

		const selectedVariant = this.elements.productForm.querySelector(
			'input[type="radio"]:checked',
		);
		const selectedVariantValue = parseInt(selectedVariant.value);

		const formData = {
			items: [
				{
					id: selectedVariantValue,
					quantity: 1,
				},
			],
		};

		try {
			const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(formData),
			});
			const data = await response.json();
			if (data.status && data.status !== 200) {
				throw new Error(data.description);
			}
			document.dispatchEvent(cartRefreshEvent);
			track({
				name: 'homepage_promo_modal',
				action: 'add-to-cart'
			});
		} catch (error) {
			console.error('Error:', error);
		}
	}
}

if (!customElements.get('hero-product-item')) {
	customElements.define('hero-product-item', HeroProduct);
}
